import {Button, CircularProgress, Divider, ListItem, TextField, Typography} from '@material-ui/core';
import {
  changeCaseNumber,
  checkACHCaseNumber,
  checkCaseNumber,
  checkCaseNumberBeneficiary,
  checkCaseNumberBillingMode,
  checkUserNameAuthJStamp
} from 'ApplicationEntry/ducks/caseDetails';
import {ApplicationType} from 'ApplicationEntry/models/ApplicationType';
import {CaseDetails, isValidCaseDetails, matchCaseDetailsByType} from 'ApplicationEntry/models/CaseDetails';
import {matchCaseNumber} from 'ApplicationEntry/models/CaseNumber';
import {isLoaded, isReserved} from 'ApplicationEntry/models/PolicyNumber';
import React, {useEffect} from 'react';
import {useDispatch} from 'react-redux';
import './CaseDetailsSection.sass';
import * as ErrorMessages from './ErrorMessages';
import './WorkFlow.sass';
import * as MA from 'ApplicationEntry/models/ModalApplication';
import {Alert} from '@material-ui/lab';
import {isLoadedPolicyTypes} from 'ApplicationEntry/models/PolicyTypes';
import {ProductRiderGroups, ProductsRidersPolicyType} from 'ApplicationEntry/models/ProductsRiders/Families';
import * as IWLActions from 'ApplicationEntry/ducks/ProductsRiders/iwl';
import * as AActions from 'ApplicationEntry/ducks/ProductsRiders/accident';
import * as DActions from 'ApplicationEntry/ducks/ProductsRiders/disability';
import {Plan, Product} from 'ApplicationEntry/models/ProductsRiders/PlanModel';
import PolicyTypesCheckboxes from './PolicyTypesCheckboxes';
import application from 'ApplicationEntry/ducks/application';

type CaseDetailsSectionProps = {
  caseDetails: CaseDetails;
  showOtherSectionsHandler: () => void;
  showOtherSections: boolean;
  modalApplication: MA.InvalidModalApplication | MA.ValidModalApplication;
  userNameJStamp: string;
};

const getApplicationType = matchCaseDetailsByType({
  Group: () => ApplicationType.Group,
  Ordinary: () => ApplicationType.Ordinary,
  NewYork: () => ApplicationType.NewYork,
});
const getMaxAllowedProductTypes = matchCaseDetailsByType({
  Group: () => 3,
  Ordinary: () => 3,
  NewYork: () => 1,
});

const CaseDetailsSection = ({
  modalApplication,
  showOtherSections,
  showOtherSectionsHandler,
  caseDetails,
  userNameJStamp,
}: CaseDetailsSectionProps) => {
  const dispatch = useDispatch();
  const {application} = modalApplication;
  const {policyNumber, productType} = caseDetails;
  const caseName = matchCaseDetailsByType({
    Group: groupApp => groupApp.caseName.value,
    _: () => '',
  })(caseDetails);

  return (
    <form className="case-details-section" noValidate autoComplete="off">
      <div className="case-details-section__title-container">
        <Typography variant="subtitle1" className="case-details-section__title-container-title">
          Case Details
        </Typography>
      </div>
      <div className="case-details-section__row">
        {matchCaseDetailsByType({
          Group: ({caseNumber}) =>
            matchCaseNumber({
              Loading: () => (
                <TextField
                  label="Case Number"
                  variant="filled"
                  placeholder="Case Number"
                  className="case-details-section__text-field"
                  value={''}
                  InputProps={{
                    endAdornment: <CircularProgress className="case-details-section__loading-policy-number-icon" />,
                  }}
                />
              ),
              Loaded: pn => (
                <TextField
                  label="Case Number"
                  variant="outlined"
                  placeholder="Case Number"
                  className="case-details-section__text-field"
                  value={pn}
                  onChange={e => {
                    dispatch(changeCaseNumber(e.target.value as string));
                  }}
                  onBlur={e => {                    
                    dispatch(checkUserNameAuthJStamp(userNameJStamp));
                    dispatch(checkCaseNumberBillingMode(e.target.value));
                    dispatch(checkCaseNumber(e.target.value));
                    dispatch(checkACHCaseNumber(e.target.value));
                    dispatch(checkCaseNumberBeneficiary(e.target.value));
                  }}
                  inputProps={{
                    maxLength: 5,
                  }}
                />
              ),
              Unassigned: () => (
                <TextField
                  label="Case Number"
                  variant="filled"
                  placeholder="Case Number"
                  className="case-details-section__text-field"
                  value={''}
                  onChange={e => {
                    dispatch(changeCaseNumber(e.target.value as string));
                  }}
                  inputProps={{
                    maxLength: 5,
                  }}
                />
              ),
              UserAssignedCaseNumber: pn => (
                <TextField
                  label="Case Number"
                  variant="outlined"
                  placeholder="Enter Case Number"
                  className="case-details-section__text-field"
                  value={pn}
                  margin="normal"
                  onChange={e => dispatch(changeCaseNumber(e.target.value as string))}
                  onBlur={e => {
                    if (e.target.value !== '') {
                      dispatch(checkUserNameAuthJStamp(userNameJStamp));
                      dispatch(checkCaseNumberBillingMode(e.target.value));
                      dispatch(checkCaseNumber(e.target.value));
                      dispatch(checkACHCaseNumber(e.target.value));                      
                      dispatch(checkCaseNumberBeneficiary(e.target.value));
                    }

                    if (application.iwl.plan.key === 'NonEmpty') {
                      let gwlPlans: Plan[] = [];
                      let productType: Product[] = [];

                      if (
                        isValidCaseDetails(application.caseDetails) &&
                        isLoadedPolicyTypes(application.caseDetails.productType)
                      ) {
                        productType = application.caseDetails.productType.selectedPolicyTypes;
                      }

                      productType.forEach(product => {
                        if (product.productGroup!.toUpperCase() === ProductRiderGroups[ProductsRidersPolicyType.Gwl]) {
                          gwlPlans = product.plans!;
                        }
                      });

                      const plan: Plan = gwlPlans.find(p => p.name! === application.iwl.plan.value)!;
                      dispatch(IWLActions.getOccClass(e.target.value, plan));
                    }

                    if (application.disability.plan.key === 'NonEmpty') {
                      let disabilityPlans: Plan[] = [];
                      let productType: Product[] = [];

                      if (
                        isValidCaseDetails(application.caseDetails) &&
                        isLoadedPolicyTypes(application.caseDetails.productType)
                      ) {
                        productType = application.caseDetails.productType.selectedPolicyTypes;
                      }

                      productType.forEach(product => {
                        if (
                          product.productGroup!.toUpperCase() ===
                          ProductRiderGroups[ProductsRidersPolicyType.Disability]
                        ) {
                          disabilityPlans = product.plans!;
                        }
                      });

                      const plan: Plan = disabilityPlans.find(p => p.name! === application.disability.plan.value)!;
                      dispatch(DActions.getSaldOccClass(e.target.value, plan));
                    }
                  }}
                  id="user-assigned-case-details-case-number"
                  inputProps={{
                    maxLength: 5,
                  }}
                />
              ),
              Error: () => (
                <TextField
                  id="err-caseNum"
                  label="Case Number"
                  variant="outlined"
                  placeholder="Enter Case Number"
                  margin="normal"
                  error
                  helperText={ErrorMessages.INVALID_CASE_NUMBER}
                  className="case-details-section__text-field"
                  value=""
                  onChange={e => dispatch(changeCaseNumber(e.target.value as string))}
                  onBlur={e => {
                    dispatch(checkCaseNumber(e.target.value));
                  }}
                />
              ),
              CaseNumberError: pn => (
                <TextField
                  error
                  id="outlined-error-helper-text"
                  label="Case Numbererror"
                  defaultValue={pn}
                  helperText={ErrorMessages.INVALID_CASE_NUMBER}
                  variant="outlined"
                  margin="normal"
                  value={pn}
                  onChange={e => dispatch(changeCaseNumber(e.target.value as string))}
                  onBlur={e => {
                    dispatch(checkCaseNumber(e.target.value));
                  }}
                  className="case-details-section__text-field"
                  placeholder="Enter Case Number"
                />
              ),
            })(caseNumber),
          _: () => null,
        })(caseDetails)}
        {caseName !== '' ? (
          <Typography variant="subtitle1" className="case-details-section__text-area">
            Case Name: {caseName}
          </Typography>
        ) : (
          ''
        )}
      </div>
      <PolicyTypesCheckboxes
        applicationType={getApplicationType(caseDetails)}
        policyTypes={productType}
        maxAllowedProductTypes={getMaxAllowedProductTypes(caseDetails)}
        policyNumber={policyNumber}
        caseDetails={caseDetails}
      />

      {application.caseDetails.key === 'ValidGroupCaseDetails' &&
      application.caseDetails?.disableNexttoShowOtherSection == false &&
      application.caseDetails.key === 'ValidGroupCaseDetails' &&
      application.caseDetails?.disableNexttoShowOtherSectionArr.every(
        (item, idx) => item.policyNumberExists == false
      ) ? (
        isReserved(policyNumber) || isLoaded(policyNumber) ? (
          <Alert severity="success" className="case-details-section__reserved-alert">
            Policy Number is reserved/assigned successfully.
          </Alert>
        ) : null
      ) : null}
      {application.caseDetails.key === 'ValidOrdinaryCaseDetails' &&
      application.caseDetails?.disableNexttoShowOtherSection == false &&
      application.caseDetails.key === 'ValidOrdinaryCaseDetails' &&
      application.caseDetails?.disableNexttoShowOtherSectionArr.every(
        (item, idx) => item.policyNumberExists == false
      ) ? (
        isReserved(policyNumber) || isLoaded(policyNumber) ? (
          <Alert severity="success" className="case-details-section__reserved-alert">
            Policy Number is reserved/assigned successfully.
          </Alert>
        ) : null
      ) : null}
      <Divider className="application-form__actions-divider" />
      {!showOtherSections && (
        <div>
          <Button
            disabled={
              !isValidCaseDetails(caseDetails) ||
              (application.caseDetails?.key === 'ValidGroupCaseDetails' &&
                application.caseDetails?.disableNexttoShowOtherSectionArr.some(item => item.policyNumberExists)) ||
              (application.caseDetails?.key === 'ValidOrdinaryCaseDetails' &&
                application.caseDetails?.disableNexttoShowOtherSectionArr.some(item => item.policyNumberExists))
            }
            variant="contained"
            className="application-wizard__next-step-button"
            onClick={showOtherSectionsHandler}
          >
            Next
          </Button>
        </div>
      )}
    </form>
  );
};

export default CaseDetailsSection;
